import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  useAppUserDetailsSelector,
  useAppUserPermissionsSelector,
  useAppUserTokenSelector,
} from "src/_common/hooks/selectors/userSelector";
import { useAppUserAction } from "src/_common/hooks/actions/user/appUserActionHook";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useEventApi } from "src/_common/hooks/actions/event/appEventApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";

import { handleApiCall, URLS, ACTIONS } from "src/_config";
import { API_BASE_URL } from "src/_config/site_urls";
import { useTranslation } from "src/_common/hooks/useTranslation";
import moment from "moment";
import axios from "axios";
import ProfileImageUpdateModal from "./ProfileImageUpdateModal";
import UserInfoSection from "./UserInfoSection";
import MemberInfoSection from "./MemberInfoSection";
import { hasPermission } from "src/_common/utils/permissionsUtils";

interface ViewProfileMainProps {}

const ViewProfileMain: React.FC<ViewProfileMainProps> = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const userSelector = useAppUserDetailsSelector();
  const userType = userSelector?.user_type || "";
  const userToken = useAppUserTokenSelector();

  // Get query parameter to determine view type
  const queryParams = new URLSearchParams(location.search);
  const viewType = queryParams.get("view") || "personal"; // 'personal' or 'company'
  const { t } = useTranslation("member");
  const userApi = useUserApi();
  const eventApi = useEventApi();
  const userAction = useAppUserAction();
  const toast = useToaster();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showImageUpdateModal, setShowImageUpdateModal] = useState(false);

  // Fetch user profile data on component mount
  const userMeCall = () => {
    handleApiCall(userApi.callGetMe, null, () => {}, toast, false, true);
  };

  useEffect(() => {
    userMeCall();
  }, []);

  // Format date for display
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return t("members.profile.personalInfo.notProvided");
    return moment(dateStr).format("Do MMMM, YYYY");
  };

  // Format phone number for display
  const formatPhoneNumber = (phone?: string | null, phoneCode?: string) => {
    if (!phone) return t("members.profile.personalInfo.notProvided");
    if (!phoneCode) return phone;
    return `+${phoneCode} ${phone}`;
  };

  // Strip HTML tags from text content
  const stripHtmlTags = (html?: string) => {
    if (!html) return t("members.profile.personalInfo.notProvided");
    return html.replace(/<[^>]*>/g, "").trim() || t("members.profile.personalInfo.notProvided");
  };

  // Handle profile image click
  const handleProfileImageClick = () => {
    setShowImageUpdateModal(true);
  };

  // Handle confirm update
  const handleConfirmUpdate = () => {
    setShowImageUpdateModal(false);
    fileInputRef.current?.click();
  };

  // Handle cancel update
  const handleCancelUpdate = () => {
    setShowImageUpdateModal(false);
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error(t("members.profile.viewProfile.messages.invalidImage"));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("members.profile.viewProfile.messages.imageSizeError"));
        return;
      }

      uploadProfileImage(file);
    }
  };

  // Upload profile image API call
  const uploadProfileImage = async (file: File) => {
    setIsUploading(true);

    try {
      // Check if user is authenticated
      if (!userToken) {
        toast.error(t("members.profile.viewProfile.messages.loginRequired"));
        setIsUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append("profile_image", file);

      // Get auth token from Redux store
      const token = userToken;

      const response = await axios.post(`${API_BASE_URL}upload-profile-image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.status === 200) {
        // Show success message from API
        toast.success(response.data.message || t("members.profile.viewProfile.messages.uploadSuccess"));

        // Update profile image in Redux state immediately
        if (response.data.data?.profile_image_url) {
          // Update user data in Redux with new profile image
          const updatedUser = {
            ...userSelector,
            profile_image: response.data.data.profile_image_url,
          };

          dispatch({
            type: ACTIONS.USER.ME,
            payload: updatedUser,
          });
        }

        // Refresh user data to get updated profile image
        userMeCall();
      } else {
        toast.error(t("members.profile.viewProfile.messages.uploadError"));
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t("members.profile.viewProfile.messages.uploadError"));
      }
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const userPermissions = useAppUserPermissionsSelector();
  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userSelector?.user_type === "member") {
      return hasPermission(userPermissions, menuName);
    }
    // Non-members (employers/admins) have access to all menus
    return true;
  };

  return (
    <div id="content" className="view-profile-content" style={{ width: "auto" }}>
      {/* Header Section */}
      <div className="profile-header">
        <div className="header-content">
          <div className="header-left">
            <Link to={URLS.DASHBOARD.MAIN} className="back-link">
              <button className="back-button">{t("members.profile.viewProfile.backButton")}</button>
            </Link>
            <h1 className="page-title">
              {viewType === "company"
                ? t("members.profile.viewProfile.companyTitle")
                : t("members.profile.viewProfile.title")}
            </h1>
          </div>
          <div className="header-right">
            {/* Show edit button for non-members OR members with edit_company permission (when viewing company) */}
            {(userType !== "member" ||
              (viewType === "company" && canAccessMenu("edit_company")) ||
              viewType === "personal") && (
              <Link
                to={`${URLS.AUTH.EDIT_PROFILE}${viewType === "company" ? "?view=company" : ""}`}
                className="edit-profile-btn"
              >
                <i className="fa fa-edit" aria-hidden="true"></i>
                <span>{t("members.profile.viewProfile.editButton")}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
      <div className="profile-content">
        <div className="container-fluid px-4">
          {/* Top Profile Header */}
          <div className="profile-header-card mb-4">
            <div className="d-flex align-items-center">
              <div className="profile-avatar me-4" style={{ position: "relative", cursor: "pointer" }}>
                <img
                  src={
                    // (userType == "member" && viewType === "personal"
                    (userType == "member"
                      ? userSelector?.member?.profile_photo_url
                      : (userSelector as any)?.company?.profile_image_url) || "/images/user-avatar.jpg"
                  }
                  alt="Profile Avatar"
                  className="profile-avatar-img"
                  onClick={handleProfileImageClick}
                  style={{
                    opacity: isUploading ? 0.7 : 1,
                    transition: "opacity 0.3s ease",
                  }}
                />
                {isUploading && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "#007bff",
                      fontSize: "20px",
                    }}
                  >
                    <i className="fa fa-spinner fa-spin"></i>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </div>
              <div className="profile-basic-info flex-grow-1">
                <h2 className="profile-name mb-1">
                  {/* {(userType == "member" && viewType === "personal" */}
                  {(userType == "member" ? userSelector?.member?.name : userSelector?.company?.name) || ""}
                  {/* : userSelector?.company?.recruiter_name) || ""} */}
                </h2>
                <p className="profile-designation mb-3">
                  {/* {userType == "member" && viewType === "personal" */}
                  {userType == "member"
                    ? userSelector?.member?.designation
                    : // : userSelector?.company?.recruiter_designation) || ""}
                      ""}
                </p>
              </div>
              <div className="profile-contact-info">
                <div className="contact-item mb-2">
                  <span className="contact-label">{t("members.profile.viewProfile.contactInfo.phoneNumber")}</span>
                  <span className="contact-value ms-2">
                    {userSelector?.user?.phone_code && userSelector?.user?.mobile_number
                      ? `${userSelector?.user?.phone_code} ${userSelector?.user?.mobile_number}`
                      : t("members.profile.personalInfo.notProvided")}
                  </span>
                </div>
                <div className="contact-item">
                  <span className="contact-label">{t("members.profile.viewProfile.contactInfo.email")}</span>
                  <span className="contact-value ms-2">
                    {userSelector?.user?.email || t("members.profile.personalInfo.notProvided")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {userType === "member" && viewType === "company" ? (
            // For members viewing company info - show non-member view
            <UserInfoSection
              userSelector={userSelector}
              userType={"company"}
              formatPhoneNumber={formatPhoneNumber}
              stripHtmlTags={stripHtmlTags}
              cityName={userSelector?.company?.city_details?.name || ""}
              locationName={userSelector?.company?.country_details?.name || ""}
            />
          ) : userType === "member" ? (
            // For members viewing personal info - show member view
            <MemberInfoSection
              userSelector={userSelector}
              formatPhoneNumber={formatPhoneNumber}
              formatDate={formatDate}
            />
          ) : (
            // For non-members - always show company info
            <UserInfoSection
              userSelector={userSelector}
              userType={userType}
              formatPhoneNumber={formatPhoneNumber}
              stripHtmlTags={stripHtmlTags}
              cityName={userSelector?.company?.city_details?.name || ""}
              locationName={userSelector?.company?.country_details?.name || ""}
            />
          )}
        </div>
      </div>
      {/* Custom Image Update Confirmation Modal */}
      {showImageUpdateModal && (
        <ProfileImageUpdateModal
          isVisible={showImageUpdateModal}
          onCancel={handleCancelUpdate}
          onConfirm={handleConfirmUpdate}
        />
      )}
    </div>
  );
};

export default ViewProfileMain;
